import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { ArrowLeft, Car, User, Phone, Mail, Lock, Loader2, CheckCircle2, AlertCircle, IdCard, Palette, Tag, Truck, Search, Copy, Send } from 'lucide-react';
interface DriverVehicleForm {
  full_name: string;
  national_id: string;
  phone: string;
  email: string;
  temporary_password: string;
  is_active: boolean;
  plate: string;
  vehicle_type: string;
  brand: string;
  model: string;
  color: string;
}
interface CreateResult {
  driver_id?: number;
  full_name?: string;
  national_id?: string;
  phone?: string;
  email?: string;
  plate?: string;
  vehicle_type?: string;
  brand?: string;
  model?: string;
  color?: string;
  vehicle_reused?: boolean;
}
const VEHICLE_TYPES = ['Carro', 'Moto', 'Bicicleta', 'Camioneta'];
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20';
export function CreateDriverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionChecked, setSessionChecked] = useState(false);
  // ─── Session guard (belt-and-suspenders) ───
  useEffect(() => {
    async function guard() {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      console.log('[CreateDriverPage] Session guard:', {
        pathname: location.pathname,
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        userId: session?.user?.id ?? null
      });
      if (!session?.access_token) {
        console.log('[CreateDriverPage] No session — redirecting to /admin/login');
        navigate('/admin/login', {
          state: {
            from: location.pathname
          },
          replace: true
        });
        return;
      }
      setSessionChecked(true);
    }
    guard();
  }, [navigate, location.pathname]);
  const [form, setForm] = useState<DriverVehicleForm>({
    full_name: '',
    national_id: '',
    phone: '',
    email: '',
    temporary_password: '',
    is_active: true,
    plate: '',
    vehicle_type: 'Carro',
    brand: '',
    model: '',
    color: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateResult | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  // Cédula lookup state
  const [cedulaChecking, setCedulaChecking] = useState(false);
  const [existingDriver, setExistingDriver] = useState<{
    full_name: string;
    phone: string;
  } | null>(null);
  const [cedulaChecked, setCedulaChecked] = useState(false);
  const cedulaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Debounced cédula lookup
  useEffect(() => {
    const val = form.national_id.trim();
    // Reset state when input changes
    setExistingDriver(null);
    setCedulaChecked(false);
    if (!val || val.length < 5) return;
    if (cedulaTimerRef.current) clearTimeout(cedulaTimerRef.current);
    cedulaTimerRef.current = setTimeout(async () => {
      setCedulaChecking(true);
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('drivers').select('full_name, phone').eq('national_id', val).maybeSingle();
        if (!queryError && data) {
          setExistingDriver({
            full_name: data.full_name,
            phone: data.phone
          });
        } else {
          setExistingDriver(null);
        }
        setCedulaChecked(true);
      } catch (err) {
        console.error('Cédula lookup error:', err);
      } finally {
        setCedulaChecking(false);
      }
    }, 400);
    return () => {
      if (cedulaTimerRef.current) clearTimeout(cedulaTimerRef.current);
    };
  }, [form.national_id]);
  const updateField = (field: keyof DriverVehicleForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.full_name.trim()) return setError('El nombre es obligatorio.');
    if (!form.national_id.trim()) return setError('La cédula es obligatoria.');
    if (!form.phone.trim()) return setError('El teléfono es obligatorio.');
    if (!form.email.trim()) return setError('El correo es obligatorio.');
    if (!form.temporary_password || form.temporary_password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres.');
    if (!form.plate.trim()) return setError('La placa del vehículo es obligatoria.');
    if (!form.vehicle_type.trim()) return setError('El tipo de vehículo es obligatorio.');
    setIsSubmitting(true);
    // Capture credentials BEFORE any async calls
    const capturedCredentials = {
      name: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.temporary_password
    };
    const payload = {
      full_name: form.full_name.trim(),
      national_id: form.national_id.trim(),
      phone: form.phone.trim(),
      email: form.email.trim().toLowerCase(),
      temporary_password: form.temporary_password,
      is_active: form.is_active,
      vehicle: {
        plate: form.plate.trim().toUpperCase(),
        vehicle_type: form.vehicle_type,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        color: form.color.trim() || null
      }
    };
    console.log('[CreateDriver] payload:', payload);
    // ─── Step 1: Force session refresh to get a fresh token ───
    console.log('[CreateDriver] Step 1: Refreshing session...');
    const {
      data: refreshData,
      error: refreshError
    } = await supabase.auth.refreshSession();
    if (refreshError) {
      console.warn('[CreateDriver] Session refresh failed:', refreshError.message, '— falling back to getSession');
    } else {
      console.log('[CreateDriver] Session refreshed successfully');
    }
    // ─── Step 2: Get the (now-fresh) session ───
    const {
      data: sessionData,
      error: sessionError
    } = await supabase.auth.getSession();
    const session = sessionData?.session;
    const accessToken = session?.access_token;
    console.log('[CreateDriver] Session state:', {
      hasSession: !!session,
      accessToken: accessToken ? 'present (' + accessToken.substring(0, 20) + '...)' : 'MISSING',
      tokenLength: accessToken?.length ?? 0,
      userId: session?.user?.id ?? null,
      email: session?.user?.email ?? null,
      expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
      isExpired: session?.expires_at ? Date.now() / 1000 > session.expires_at : 'no session',
      secondsUntilExpiry: session?.expires_at ? Math.round(session.expires_at - Date.now() / 1000) : null,
      sessionError: sessionError?.message ?? null,
      refreshError: refreshError?.message ?? null
    });
    if (!accessToken) {
      console.error('[CreateDriver] NO ACCESS TOKEN — cannot call edge function');
      setError('No hay sesión activa. Inicia sesión primero en /admin/login');
      setIsSubmitting(false);
      return;
    }
    // ─── Step 3: Verify token is valid with getUser() before sending ───
    console.log('[CreateDriver] Step 3: Verifying token with getUser()...');
    const {
      data: userData,
      error: userError
    } = await supabase.auth.getUser();
    console.log('[CreateDriver] getUser() result:', {
      success: !!userData?.user && !userError,
      userId: userData?.user?.id ?? null,
      email: userData?.user?.email ?? null,
      error: userError?.message ?? null
    });
    if (userError || !userData?.user) {
      console.error('[CreateDriver] Token is INVALID — getUser() failed:', userError?.message);
      setError('Tu sesión ha expirado. Por favor inicia sesión de nuevo.');
      setIsSubmitting(false);
      // Force re-login
      navigate('/admin/login', {
        state: {
          from: location.pathname
        },
        replace: true
      });
      return;
    }
    // ─── Step 4: Build and send request ───
    const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/create-driver-with-vehicle`;
    const requestHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY
    };
    console.log('[CreateDriver] Request details:', {
      url: edgeFunctionUrl,
      method: 'POST',
      authHeaderPrefix: `Bearer ${accessToken.substring(0, 20)}...`,
      authHeaderLength: `Bearer ${accessToken}`.length,
      apiKeyPrefix: SUPABASE_ANON_KEY.substring(0, 20) + '...',
      tokenIsJWT: accessToken.startsWith('eyJ')
    });
    let data: any = null;
    try {
      const res = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(payload)
      });
      data = await res.json();
      console.log('[CreateDriver] Response:', {
        status: res.status,
        statusText: res.statusText,
        data
      });
      if (!res.ok) {
        console.error('backend error:', data);
        const raw = (data?.error || data?.details || data?.message || '').toLowerCase();
        let friendlyMsg = data?.error || data?.details || data?.message || `Error del servidor (${res.status})`;
        if (raw.includes('national_id') || raw.includes('cédula') || raw.includes('duplicate') && raw.includes('cedula')) {
          friendlyMsg = 'Ya existe un conductor registrado con esta cédula.';
        } else if (raw.includes('phone') || raw.includes('teléfono') || raw.includes('telefono')) {
          friendlyMsg = 'Ya existe un conductor registrado con este teléfono.';
        } else if (raw.includes('email') || raw.includes('correo') || raw.includes('already registered') || raw.includes('already been registered')) {
          friendlyMsg = 'Ya existe un usuario con este correo.';
        }
        setError(friendlyMsg);
        setIsSubmitting(false);
        return;
      }
    } catch (fetchErr: any) {
      console.error('fetch error:', fetchErr);
      setError(fetchErr.message || 'Error de conexión con el servidor.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    if (!data?.success) {
      setError(data?.error || data?.details || 'Error al crear el conductor.');
      return;
    }
    setCreatedCredentials(capturedCredentials);
    setResult({
      driver_id: data.driver?.id,
      full_name: data.driver?.full_name || capturedCredentials.name,
      national_id: data.driver?.national_id || form.national_id,
      phone: data.driver?.phone || form.phone,
      email: data.auth_user?.email || capturedCredentials.email,
      plate: data.vehicle?.plate || form.plate,
      vehicle_type: data.vehicle?.vehicle_type || form.vehicle_type,
      brand: data.vehicle?.brand || form.brand,
      model: data.vehicle?.model || form.model,
      color: data.vehicle?.color || form.color,
      vehicle_reused: data.vehicle_reused === true
    });
  };
  const [copied, setCopied] = useState(false);
  const getCredentialsText = () => {
    if (!createdCredentials) return '';
    return `Hola ${createdCredentials.name},\n\nTu cuenta de conductor en NOIZA ha sido creada.\n\nEmail: ${createdCredentials.email}\nContraseña temporal: ${createdCredentials.password}\n\nPor favor cambia tu contraseña después de tu primer inicio de sesión.\n\nEquipo NOIZA`;
  };
  const handleCopyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(getCredentialsText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = getCredentialsText();
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  const handleSendByEmail = () => {
    if (!createdCredentials) return;
    const subject = encodeURIComponent('Acceso a tu cuenta de conductor en NOIZA');
    const body = encodeURIComponent(`Hola ${createdCredentials.name},\n\n` + `Tu cuenta de conductor en NOIZA ha sido creada exitosamente.\n\n` + `Datos de acceso:\n` + `Email: ${createdCredentials.email}\n` + `Contraseña temporal: ${createdCredentials.password}\n\n` + `Importante: Por favor cambia tu contraseña después de tu primer inicio de sesión.\n\n` + `¡Bienvenido al equipo!\n` + `Equipo NOIZA`);
    window.open(`mailto:${createdCredentials.email}?subject=${subject}&body=${body}`, '_blank');
  };
  if (!sessionChecked) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3305" data-mp="1w4pbys7g62we">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-3306" data-mp="2141ytcybj9yz" />
      </div>;
  }
  if (result) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-3307" data-mp="2ote8edxk5k1t">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-3308" data-mp="150cvx23j7y55">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-3309" data-mp="151o3ze40fs22">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-3310" data-mp="103e811agonfe" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-3311" data-mp="w081dsjfsu8e">¡Conductor creado!</h1>
          <p className="text-white/50" data-id="element-3312" data-mp="r1dby7qys7fb">
            Cuenta, vehículo y asignación creados exitosamente.
          </p>

          {/* Credentials Card */}
          <div className="bg-[#141414] border border-[#006847]/30 rounded-2xl p-5 text-left space-y-4" data-id="element-3313" data-mp="1nsytbqu37wru">
            <div className="flex items-center justify-between" data-id="element-3314" data-mp="1jrmyn6wcwlcs">
              <p className="text-xs font-bold text-[#00a870] uppercase tracking-widest" data-id="element-3315" data-mp="1ewzibvbiax9a">
                Credenciales de acceso
              </p>
              <Lock className="w-4 h-4 text-[#00a870]/50" data-id="element-3316" data-mp="27ev5ht9fo2gx" />
            </div>
            <div className="space-y-3 text-sm" data-id="element-3317" data-mp="27gy3azgtuzw7">
              <div className="flex justify-between" data-id="element-3318" data-mp="22nv3wapdcw83">
                <span className="text-white/50" data-id="element-3319" data-mp="22maawpipdlcc">Nombre</span>
                <strong data-id="element-3320" data-mp="2a0kxwetuxaac">{createdCredentials?.name}</strong>
              </div>
              <div className="flex justify-between" data-id="element-3321" data-mp="2exkttwwzswnq">
                <span className="text-white/50" data-id="element-3322" data-mp="2f0gtbpohntea">Email</span>
                <strong className="font-mono text-xs" data-id="element-3323" data-mp="2jweb80feh7t6">
                  {createdCredentials?.email}
                </strong>
              </div>
              <div className="flex justify-between items-center" data-id="element-3324" data-mp="2nwpsd38k0pzw">
                <span className="text-white/50" data-id="element-3325" data-mp="2sxm586avvmhg">Contraseña temporal</span>
                <strong className="font-mono text-xs bg-white/5 px-2 py-1 rounded-lg" data-id="element-3326" data-mp="2xwgm32paj0yx">
                  {createdCredentials?.password}
                </strong>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2" data-id="element-3327" data-mp="37m8k66wlmc85">
              <button onClick={handleCopyCredentials} className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-95 ${copied ? 'bg-[#006847]/20 text-[#00a870] border border-[#006847]/30' : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'}`} data-id="element-3328" data-mp="3bowqo7xub71y">
                {copied ? <>
                    <CheckCircle2 className="w-4 h-4" data-id="element-3329" data-mp="1qcbjjpyqib8u" /> Copiado
                  </> : <>
                    <Copy className="w-4 h-4" data-id="element-3330" data-mp="1lj8qdixvd3t6" /> Copiar credenciales
                  </>}
              </button>
              <button onClick={handleSendByEmail} className="flex-1 py-2.5 bg-[#006847] hover:bg-[#005a3d] rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-95" data-id="element-3331" data-mp="1vad4yhjmumnh">
                <Send className="w-4 h-4" data-id="element-3332" data-mp="24bugdmm3i05u" /> Enviar por email
              </button>
            </div>
          </div>

          {/* Driver info */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-3333" data-mp="9xa33v03t6wh">
            <p className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1" data-id="element-3334" data-mp="2ljvgjknxed48">
              Conductor
            </p>
            <div className="flex justify-between" data-id="element-3335" data-mp="2qiyk10o9cba3">
              <span className="text-white/50" data-id="element-3336" data-mp="2csbjr42umdbw">Cédula</span>
              <strong className="font-mono text-xs" data-id="element-3337" data-mp="2crtg3mduwwty">
                {result.national_id}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-3338" data-mp="22tvu75jgzc4i">
              <span className="text-white/50" data-id="element-3339" data-mp="22t4aarz8j2u2">Teléfono</span>
              <strong data-id="element-3340" data-mp="27sp4yq7dgp0z">{result.phone}</strong>
            </div>
            {result.driver_id && <div className="flex justify-between" data-id="element-3341" data-mp="1tth843yolszy">
                <span className="text-white/50" data-id="element-3342" data-mp="1tv0kvkl4plwz">Driver ID</span>
                <strong className="font-mono" data-id="element-3343" data-mp="1xu1597j98wm3">{result.driver_id}</strong>
              </div>}
          </div>

          {/* Vehicle info */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-3344" data-mp="3o5e2a4oidcyp">
            <div className="flex items-center justify-between mb-1" data-id="element-3345" data-mp="2fzgxe4njol2v">
              <p className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-3346" data-mp="2fyp10rm4ijbj">
                Vehículo
              </p>
              {result.vehicle_reused ? <span className="text-xs font-bold bg-yellow-500/15 text-yellow-400 px-2.5 py-0.5 rounded-lg" data-id="element-3347" data-mp="268m1s9oes94r">
                  Reutilizado
                </span> : <span className="text-xs font-bold bg-[#006847]/20 text-[#00a870] px-2.5 py-0.5 rounded-lg" data-id="element-3348" data-mp="1x98zc0ypcktj">
                  Nuevo
                </span>}
            </div>
            <div className="flex justify-between" data-id="element-3349" data-mp="1na9nk9in492b">
              <span className="text-white/50" data-id="element-3350" data-mp="1nb1jxmk2aatn">Placa</span>
              <strong className="font-mono" data-id="element-3351" data-mp="1ichz5ph7f52v">{result.plate}</strong>
            </div>
            <div className="flex justify-between" data-id="element-3352" data-mp="3hokh2va6jref">
              <span className="text-white/50" data-id="element-3353" data-mp="3ho165yiy639w">Tipo</span>
              <strong data-id="element-3354" data-mp="3cphjfvwj4ngh">{result.vehicle_type}</strong>
            </div>
            {result.brand && <div className="flex justify-between" data-id="element-3355" data-mp="3l7jix7f8wrmi">
                <span className="text-white/50" data-id="element-3356" data-mp="3l4nh380a1iss">Marca</span>
                <strong data-id="element-3357" data-mp="3p7a6z3de0tnc">{result.brand}</strong>
              </div>}
            {result.model && <div className="flex justify-between" data-id="element-3358" data-mp="3tzlh0z1udvtm">
                <span className="text-white/50" data-id="element-3359" data-mp="2shas6diudm9">Modelo</span>
                <strong data-id="element-3360" data-mp="2rz9gvc0597h">{result.model}</strong>
              </div>}
            {result.color && <div className="flex justify-between" data-id="element-3361" data-mp="32a8j9lppbw6j">
                <span className="text-white/50" data-id="element-3362" data-mp="36ainotxuieja">Color</span>
                <strong data-id="element-3363" data-mp="3b8ka9ouhcvzi">{result.color}</strong>
              </div>}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-left" data-id="element-3364" data-mp="3g7nfb9agn27h">
            <p className="text-sm text-yellow-300" data-id="element-3365" data-mp="qiblahadbq3c">
              ⚠️ La contraseña temporal solo se muestra ahora. Asegúrate de
              compartirla de forma segura. El conductor debe cambiarla en su
              primer inicio de sesión.
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-3366" data-mp="27a5a88hwnzfb">
            <button onClick={() => {
            setResult(null);
            setCreatedCredentials(null);
            setCopied(false);
            setForm({
              full_name: '',
              national_id: '',
              phone: '',
              email: '',
              temporary_password: '',
              is_active: true,
              plate: '',
              vehicle_type: 'Carro',
              brand: '',
              model: '',
              color: ''
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-3367" data-mp="2casw6fra1q2o">
              Crear otro
            </button>
            <button onClick={() => navigate('/admin/drivers')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-3368" data-mp="3ds1tl8l5jnw3">
              Ver conductores
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-3369" data-mp="tbv07w9fiudc">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-3370" data-mp="odl4nkjslrr7">
        <DriverAdminTabs data-id="element-3371" data-mp="odbgm2ib5uo5" />

        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-3372" data-mp="jf07jtzu7o6z">
          <button onClick={() => navigate('/admin/drivers')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-3373" data-mp="jcdxnnph4wll">
            <ArrowLeft className="w-5 h-5" data-id="element-3374" data-mp="5ez4q900i6ov" />
          </button>
          <div data-id="element-3375" data-mp="5gscjrmd2wyg">
            <h1 className="text-2xl font-bold" data-id="element-3376" data-mp="5h20zarrcw21">Nuevo Conductor</h1>
            <p className="text-white/40 text-sm" data-id="element-3377" data-mp="24rbdt5prr67p">
              Registra conductor y vehículo en un solo paso
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3378" data-mp="2iwtsiet96xcy">
          {/* SECTION 1 — Driver */}
          <div className="space-y-4" data-id="element-3379" data-mp="2nollevzvtf69">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-3380" data-mp="2npmx92oq25p1">
              <User className="w-4 h-4 text-[#00a870]" data-id="element-3381" data-mp="29xq7h5aj07vt" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-3382" data-mp="2evrrbsfs0b8c">
                Datos del conductor
              </h2>
            </div>

            {/* Name */}
            <div data-id="element-3383" data-mp="32ji0nt63z2su">
              <label className={labelClass} data-id="element-3384" data-mp="36mo4ku5r0hw5">Nombre completo</label>
              <div className="relative" data-id="element-3385" data-mp="36ktt06gda24q">
                <User className={iconClass} data-id="element-3386" data-mp="2snogbnm5e50i" />
                <input type="text" value={form.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Juan Pérez" className={inputClass} required data-id="element-3387" data-mp="2smwu33efxjmw" />
              </div>
            </div>

            {/* National ID with live lookup */}
            <div data-id="element-3388" data-mp="1em8wf3dm64ne">
              <label className={labelClass} data-id="element-3389" data-mp="1elqr7h8z4g3c">Cédula</label>
              <div className="relative" data-id="element-3390" data-mp="19p9zkd2kfjli">
                <IdCard className={iconClass} data-id="element-3391" data-mp="19ql44f3u4vdo" />
                <input type="text" value={form.national_id} onChange={e => updateField('national_id', e.target.value)} placeholder="Ej: 304590658" className={`${inputClass} ${existingDriver ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/50' : ''}`} required data-id="element-3392" data-mp="1norr3078xayk" />
                {cedulaChecking && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" data-id="element-3393" data-mp="14qytmdfe5t8k" />}
                {!cedulaChecking && cedulaChecked && !existingDriver && <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00a870]" data-id="element-3394" data-mp="c706p3qifgj0" />}
              </div>

              {/* Cédula lookup feedback */}
              {cedulaChecking && <p className="text-xs text-white/30 mt-1.5 pl-1" data-id="element-3395" data-mp="pvtl19b7mpad">
                  Verificando cédula...
                </p>}
              {!cedulaChecking && cedulaChecked && existingDriver && <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3396" data-mp="zm4ypnkp4cug">
                  <p className="text-sm font-bold text-red-400" data-id="element-3397" data-mp="13ngpvxj7x64r">
                    Ya existe un conductor registrado con esta cédula
                  </p>
                  <div className="mt-2 space-y-1 text-xs text-red-300/70" data-id="element-3398" data-mp="18kgnrl5wz2ks">
                    <p data-id="element-3399" data-mp="1dmpialyfqcp0">
                      Nombre:{' '}
                      <strong className="text-red-300" data-id="element-3400" data-mp="1ik7innydzjle">
                        {existingDriver.full_name}
                      </strong>
                    </p>
                    <p data-id="element-3401" data-mp="3felr3nt7x8g3">
                      Teléfono:{' '}
                      <strong className="text-red-300" data-id="element-3402" data-mp="3kb2iqrzmm4xx">
                        {existingDriver.phone}
                      </strong>
                    </p>
                  </div>
                </div>}
              {!cedulaChecking && cedulaChecked && !existingDriver && <p className="text-xs text-[#00a870]/70 mt-1.5 pl-1 flex items-center gap-1" data-id="element-3403" data-mp="1snw95o8v3z7d">
                  <CheckCircle2 className="w-3 h-3" data-id="element-3404" data-mp="1npusf9ywsbz2" />
                  No existe un conductor con esta cédula
                </p>}
            </div>

            {/* Phone */}
            <div data-id="element-3405" data-mp="2ge9w50clb7v3">
              <label className={labelClass} data-id="element-3406" data-mp="2gdqpifse45wd">Teléfono</label>
              <div className="relative" data-id="element-3407" data-mp="2gc5u6ny94ixg">
                <Phone className={iconClass} data-id="element-3408" data-mp="2be47lt1ma1h8" />
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="88888888" className={inputClass} required data-id="element-3409" data-mp="2bh089p4un49d" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-3410" data-mp="whksxp7shxh2">
              <label className={labelClass} data-id="element-3411" data-mp="wj49zi19scjw">Correo electrónico</label>
              <div className="relative" data-id="element-3412" data-mp="11h5vsaq2ypz2">
                <Mail className={iconClass} data-id="element-3413" data-mp="11g4jk2xbvxfr" />
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="conductor@noiza.com" className={inputClass} required data-id="element-3414" data-mp="nho1xgxxgmiu" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-3415" data-mp="ik4lcadrbxnq">
              <label className={labelClass} data-id="element-3416" data-mp="4qn2krswaoyr">Contraseña temporal</label>
              <div className="relative" data-id="element-3417" data-mp="4ok5jntbrvkj">
                <Lock className={iconClass} data-id="element-3418" data-mp="8rqmbpe2d559" />
                <input type="text" value={form.temporary_password} onChange={e => updateField('temporary_password', e.target.value)} placeholder="Mínimo 6 caracteres" className={`${inputClass} font-mono`} required minLength={6} data-id="element-3419" data-mp="8si5g0qh5aen" />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-3420" data-mp="3ma3sahw1no12">
              <span className="text-sm font-medium text-white/70" data-id="element-3421" data-mp="3h8xxmeitpqow">
                Conductor activo
              </span>
              <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-3422" data-mp="3v7nxu30xw6hh">
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-3423" data-mp="3vutmpknydmn" />
              </button>
            </div>
          </div>

          {/* SECTION 2 — Vehicle */}
          <div className="space-y-4" data-id="element-3424" data-mp="1k7oyfa195nh1">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-3425" data-mp="1kaul6imesd9l">
              <Car className="w-4 h-4 text-[#00a870]" data-id="element-3426" data-mp="1p5qinyaezsu3" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-3427" data-mp="1u6ehh64vxdyv">
                Datos del vehículo
              </h2>
            </div>

            {/* Plate */}
            <div data-id="element-3428" data-mp="11iil62j89o2m">
              <label className={labelClass} data-id="element-3429" data-mp="11j1rsn3fgq1c">Placa</label>
              <div className="relative" data-id="element-3430" data-mp="16h3cfcgi4xex">
                <Tag className={iconClass} data-id="element-3431" data-mp="16i4y0av592aw" />
                <input type="text" value={form.plate} onChange={e => updateField('plate', e.target.value.toUpperCase())} placeholder="Ej: BRC713" className={`${inputClass} font-mono uppercase`} required data-id="element-3432" data-mp="16epix81gzt9h" />
              </div>
            </div>

            {/* Vehicle Type */}
            <div data-id="element-3433" data-mp="f9tnr12kry1k">
              <label className={labelClass} data-id="element-3434" data-mp="f8a89coqtr0u">Tipo de vehículo</label>
              <div className="relative" data-id="element-3435" data-mp="a9yy10ujdei3">
                <Truck className={iconClass} data-id="element-3436" data-mp="ab07x1ztfuy8" />
                <select value={form.vehicle_type} onChange={e => updateField('vehicle_type', e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required data-id="element-3437" data-mp="68n3qi0pw53k">
                  {VEHICLE_TYPES.map(type => <option key={type} value={type} className="bg-[#141414] text-white" data-id="element-3438" data-mp="3np4y9rkfzgxi">
                      {type}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Brand */}
            <div data-id="element-3439" data-mp="39ou68l7ebwar">
              <label className={labelClass} data-id="element-3440" data-mp="39rgekn242fu1">
                Marca{' '}
                <span className="text-white/20 normal-case" data-id="element-3441" data-mp="34raupt231xmi">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3442" data-mp="2dmwlfcsxztw0">
                <Car className={iconClass} data-id="element-3443" data-mp="29nec7eahh790" />
                <input type="text" value={form.brand} onChange={e => updateField('brand', e.target.value)} placeholder="Ej: Toyota" className={inputClass} data-id="element-3444" data-mp="29mcrsj7kv8em" />
              </div>
            </div>

            {/* Model */}
            <div data-id="element-3445" data-mp="1uw5d8ekoy9j7">
              <label className={labelClass} data-id="element-3446" data-mp="1pz56s2jxj4rk">
                Modelo{' '}
                <span className="text-white/20 normal-case" data-id="element-3447" data-mp="1px2ax1w3ihex">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3448" data-mp="24q4elbcxxpa3">
                <Car className={iconClass} data-id="element-3449" data-mp="1zwt4908x6qdf" />
                <input type="text" value={form.model} onChange={e => updateField('model', e.target.value)} placeholder="Ej: Corolla" className={inputClass} data-id="element-3450" data-mp="1zw06upvupd7y" />
              </div>
            </div>

            {/* Color */}
            <div data-id="element-3451" data-mp="3nmpkeuevn4ba">
              <label className={labelClass} data-id="element-3452" data-mp="3nn93ieg9jyqw">
                Color{' '}
                <span className="text-white/20 normal-case" data-id="element-3453" data-mp="3nok98jt9rgkn">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3454" data-mp="39thud76g2gvp">
                <Palette className={iconClass} data-id="element-3455" data-mp="3dw4s1opsqw0t" />
                <input type="text" value={form.color} onChange={e => updateField('color', e.target.value)} placeholder="Ej: Blanco" className={inputClass} data-id="element-3456" data-mp="3dutnvnsfvm96" />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3457" data-mp="1e6gq7ta6hvj">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3458" data-mp="2h5c8gh3nmlua" />
              <div className="text-sm whitespace-pre-line" data-id="element-3459" data-mp="2m6rsq6y0cobm">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting || !!existingDriver} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-3460" data-mp="rp03hvtcmidf">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-3461" data-mp="8ztiu38zvgoz" />
                Creando conductor y vehículo...
              </> : existingDriver ? <>
                <AlertCircle className="w-5 h-5" data-id="element-3462" data-mp="3v5gqx867tqjg" />
                Conductor ya existe
              </> : <>
                <User className="w-5 h-5" data-id="element-3463" data-mp="3r6g83pnqmnwg" />
                Crear Conductor con Vehículo
              </>}
          </button>
        </form>
      </div>
    </div>;
}